import api from '@api/index'
import {
  FuelModelDTO,
  IdentifiedFuelModelDTO
} from '@models/api/simulation/FuelModelDTO'
import { FuelModelHumidityDTO } from '@models/api/simulation/FuelModelHumidityDTO'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useSimulationStore } from './simulationStore'
import { MeteoConditionsID } from '@enums/MeteoConditionsID'
import { showToastError } from '@services/ToastService'
import { useI18n } from 'vue-i18n'
import { FuelModelLoadDTO } from '@models/api/simulation/FuelModelLoadDTO'
import { MathHelper } from '@helpers/MathHelper'

const MAX_HUMIDITY = 300
const MIN_HUMIDITY = 2

export const useSimulationFuelModelsStore = defineStore(
  'simulationFuelModels',
  () => {
    const { t } = useI18n()
    const appliedModelsHumidity = ref<FuelModelHumidityDTO[]>()
    const fuelModelsPresets = ref<IdentifiedFuelModelDTO[]>([])
    const defaultFuelModelPreset = ref<IdentifiedFuelModelDTO>()
    const appliedFuelModelLoads = ref<FuelModelLoadDTO[]>()
    const editedFuelModelPreset = ref<IdentifiedFuelModelDTO>()
    const selectedFuelModelLoads = computed(() => {
      if (defaultFuelModelPreset.value) {
        return defaultFuelModelPreset.value.fuelModelFileData
      }
      return []
    })

    function getPresetWithFuelModels(
      fuelModels?: FuelModelLoadDTO[]
    ): IdentifiedFuelModelDTO | undefined {
      return fuelModelsPresets.value.find((preset) => {
        return (
          JSON.stringify(preset.fuelModelFileData) ===
          JSON.stringify(fuelModels)
        )
      })
    }

    async function fetchFuelModels() {
      fuelModelsPresets.value = (
        await api.fuelModelFile.getFuelModelFileFromOrg()
      ).data
      try {
        defaultFuelModelPreset.value = (
          await api.fuelModelFile.getDefaultPreset()
        ).data[0]
        if (!appliedFuelModelLoads.value) {
          appliedFuelModelLoads.value =
            defaultFuelModelPreset.value.fuelModelFileData
        }
      } catch (error) {
        console.error('Failed to fetch default fuel model preset', error)
        showToastError(
          t('error'),
          t('simulationFuelModelsStore.errorGetDefaultPreset')
        )
      }
      if (defaultFuelModelPreset.value) {
        defaultFuelModelPreset.value = fuelModelsPresets.value.find(
          (preset) =>
            preset.ID_FUEL_MODEL_FILE ===
            defaultFuelModelPreset.value?.ID_FUEL_MODEL_FILE
        )
      }
      return fuelModelsPresets.value
    }

    async function deleteFuelModelLoadPreset(
      fuelModel: IdentifiedFuelModelDTO
    ) {
      try {
        await api.fuelModelFile.deleteFuelModelFile(
          fuelModel.ID_FUEL_MODEL_FILE
        )
        fuelModelsPresets.value = fuelModelsPresets.value.filter(
          (preset) => preset.ID_FUEL_MODEL_FILE !== fuelModel.ID_FUEL_MODEL_FILE
        )
      } catch (error) {
        console.error('Failed to delete fuel model load preset', error)
        throw error
      }
    }

    function setAppliedFuelModelLoads(fuelModelLoads: FuelModelLoadDTO[]) {
      appliedFuelModelLoads.value = fuelModelLoads
      calculateAndApplyHumidity()
    }

    async function createFuelModelLoadPreset(fuelModel: FuelModelDTO) {
      try {
        const response =
          await api.fuelModelFile.insertFuelModelFileForOrg(fuelModel)
        return response.data
      } catch (error) {
        console.error('Failed to create fuel model load preset', error)
        throw error
      }
    }

    function isFuelModelsApplied(
      fuelModelLoads: (FuelModelLoadDTO | Partial<FuelModelLoadDTO>)[]
    ) {
      if (appliedFuelModelLoads.value) {
        return (
          JSON.stringify(appliedFuelModelLoads.value) ===
          JSON.stringify(fuelModelLoads)
        )
      }
      return false
    }

    function calculateAndApplyHumidity() {
      const meteoConditions = useSimulationStore().simulationCustomConditions
      const relativeHumidity = meteoConditions?.find(
        (x) => x.id === MeteoConditionsID.RELATIVE_HUMIDITY
      )?.values[0]
      if (appliedFuelModelLoads.value && relativeHumidity) {
        console.log('Recalculando humedades')
        const newHumidity = appliedFuelModelLoads.value!.map((fuelLoad) => {
          const baseHumidity = relativeHumidity / 5
          const humidityModel: FuelModelHumidityDTO = {
            model: fuelLoad.FUEL_MODEL_TYPE,
            humidity: MathHelper.adjustToLimits(
              Math.floor(baseHumidity),
              MIN_HUMIDITY,
              MAX_HUMIDITY
            ),
            humidity10h: MathHelper.adjustToLimits(
              Math.floor(baseHumidity + 1),
              MIN_HUMIDITY,
              MAX_HUMIDITY
            ),
            humidity100h: MathHelper.adjustToLimits(
              Math.floor(baseHumidity + 2),
              MIN_HUMIDITY,
              MAX_HUMIDITY
            ),
            FLW: MathHelper.adjustToLimits(
              Math.floor(baseHumidity * 10),
              MIN_HUMIDITY,
              MAX_HUMIDITY
            ),
            FLH: MathHelper.adjustToLimits(
              Math.floor((baseHumidity + 2 + 1) * 10),
              MIN_HUMIDITY,
              MAX_HUMIDITY
            )
          }
          return humidityModel
        })
        appliedModelsHumidity.value = newHumidity
      }
    }

    async function setDefaultFuelModelPreset(
      fuelModel: IdentifiedFuelModelDTO
    ) {
      try {
        await api.fuelModelFile.setDefaultPreset(fuelModel.ID_FUEL_MODEL_FILE)
        defaultFuelModelPreset.value = fuelModel
      } catch (error) {
        console.error('Failed to set default fuel model preset', error)
        showToastError(
          t('error'),
          t('simulationFuelModelsStore.errorSetDefaultPreset')
        )
      }
    }

    async function saveFuelModelLoadInOrganization(
      fuelModel: IdentifiedFuelModelDTO
    ) {
      if (!defaultFuelModelPreset.value) {
        console.error('No fuel model preset selected')
        return
      }
      defaultFuelModelPreset.value.fuelModelFileData =
        fuelModel.fuelModelFileData
      return await api.fuelModelFile.updateFuelModelFile(fuelModel)
    }

    return {
      defaultFuelModelPreset,
      selectedFuelModelLoads,
      fuelModelsPresets,
      appliedModelsHumidity,
      appliedFuelModelLoads,
      getPresetWithFuelModels,
      saveFuelModelLoadInOrganization,
      fetchFuelModels,
      createFuelModelLoadPreset,
      calculateAndApplyDefaultHumidity: calculateAndApplyHumidity,
      setDefaultFuelModelPreset,
      setAppliedFuelModelLoads,
      isFuelModelsApplied,
      deleteFuelModelLoadPreset,
      editedFuelModelPreset
    }
  }
)
